/// <amd-module name="Pipeline/Analytics/PipelineOutcome/Common/Sources/PipelineOutcomeReportSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { IReportContext, TrendBy } from "Analytics/Shared/Common/Types";
import { getDateFilter } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { ITrendChartAggregateData, ITrendChartData, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { DateSKParser } from "Analytics/Shared/Utils/DateSKParser";
import { useMockData } from "Pipeline/Analytics/Common/MockReportData";
import * as CommonResources from "Pipeline/Analytics/Common/Resources";
import { PipelineTrendSource } from "Pipeline/Analytics/Common/Sources/PipelineTrendSource";
import {
    IPipelineOutcomeData,
    IPipelineOutcomePassRateData,
    IPipelineOutcomeReportConfiguration,
    IStageMetrics
} from "Pipeline/Analytics/Common/Types";
import { getMockTaskTrendData } from "Pipeline/Analytics/PipelineOutcome/Common/MockData";
import { IDailyTaskFailureData, IFailingTaskData, IPipelineOutcomeTaskFailureData } from "Pipeline/Analytics/PipelineOutcome/Common/Types";

interface IODataResult {
    BuildCompletedDateSK?: number;
    PipelineRunCompletedDateSK?: number;
    TaskDisplayName: string;
    FailedCount: number;
}

interface IODataStageFailureTrend {
    BuildCompletedDateSK?: number;
    PipelineRunCompletedDateSK?: number;
    FailedStageCount: number;
    PipelineJob: {
        StageName: string;
    };
}

export abstract class PipelineOutcomeReportSource extends PipelineTrendSource {
    public async queryTaskFailureSummaryAndTrendData(
        reportContext: IReportContext,
        confValues: IPipelineOutcomeReportConfiguration
    ): Promise<IPipelineOutcomeTaskFailureData> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        const filterStr = confValues.stage
            ? super._getTopFailingTaskQueryFilterForStage(reportContext, confValues, confValues.stage.fullName)
            : super._getTopFailingTaskQueryFilter(reportContext, confValues);
        const groupByStr = `groupby((${this._getGroupByTask()}, ${
            PipelineEntities.PipelineRunCompletedDateSK
        }), aggregate(${this._getFailedCountForAggregation()}))`;
        queryOptions.$apply = `${filterStr}/${groupByStr}`;
        queryOptions.$orderby = ` ${PipelineEntities.PipelineRunCompletedDateSK} asc `;
        return this._queryTaskFailureSummaryAndTrendData(queryOptions);
    }

    public queryPipelineSummaryAndTrendData(
        reportContext: IReportContext,
        confValues: ITrendChartReportConfiguration
    ): Promise<IPipelineOutcomeData> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildEntityName,
            project: projectId
        } as ODataQueryOptions;

        let filterStr = `filter(${this._getPipelineContextFilter(reportContext)} ${this._getBranchFilter(confValues.branchItems)} and ${getDateFilter(
            TrendBy.Days,
            confValues.period
        )} and ${this._getTotalPipelineFilter()})`;
        const groupByStr = `groupby((${this._getGroupByCompletedDate()}), aggregate(${this._getTotalCountForAggregation()}, ${this._getPartiallySucceededCountForAggregation()}, ${this._getSucceededCountForAggregation()}, ${this._getFailedCountForAggregation()}))`;
        queryOptions.$apply = `${filterStr}/${groupByStr}`;
        queryOptions.$orderby = ` CompletedDateSK asc `;
        const passRateTrendPromise = this._queryPassRateTrendData(queryOptions, confValues);

        queryOptions.entityType = this._buildTaskResultsEntityName;
        filterStr = this._getTopFailingTaskQueryFilter(reportContext, confValues);
        const groupByPrimaryStr = `groupby((${this._getBuildCompletedDateSK()}, ${this._getGroupByBuildId()}, ${this._getPipelineJobStageName()}), aggregate(${this._getFailedCountForAggregation()}))`;
        const groupBySecondaryStr = `groupby((${this._getBuildCompletedDateSK()}, ${this._getPipelineJobStageName()}), aggregate(${this._getFailedStageCountForAggregation()}))`;
        queryOptions.$apply = `${filterStr}/${groupByPrimaryStr}/${groupBySecondaryStr}`;
        queryOptions.$orderby = ` ${this._getBuildCompletedDateSK()} asc `;
        const stageFailureTrendPromise = this._queryStageFailureTrendData(queryOptions, confValues);

        return Promise.all([passRateTrendPromise, stageFailureTrendPromise]).then(([passRateTrend, stageFailureTrend]) => {
            if (stageFailureTrend && Object.keys(stageFailureTrend.trendChartData.primaryChartData).length > 0) {
                passRateTrend.trendChartData.primaryChartData = stageFailureTrend.trendChartData.primaryChartData;
                passRateTrend.pipelineOutcomePassRateData.failurePerStage = stageFailureTrend.pipelineOutcomePassRateData.failurePerStage;
            }
            return passRateTrend;
        });
    }

    public async queryPipelinePassRateTrendData(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): Promise<ITrendChartData> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildEntityName,
            project: projectId
        } as ODataQueryOptions;

        const filterStr = `filter(${this._getPipelineContextFilter(reportContext)} ${this._getBranchFilter(
            confValues.branchItems
        )} and ${getDateFilter(TrendBy.Days, confValues.period)} and ${this._getTotalPipelineFilter()})`;
        const groupByStr = `groupby((${this._getGroupByCompletedDate()}), aggregate(${this._getTotalCountForAggregation()}, ${this._getPartiallySucceededCountForAggregation()}, ${this._getSucceededCountForAggregation()}, ${this._getFailedCountForAggregation()}))`;
        queryOptions.$apply = `${filterStr}/${groupByStr}`;
        queryOptions.$orderby = ` CompletedDateSK asc `;

        const pipelineTrendData = await this._queryPassRateTrendData(queryOptions, confValues);
        return pipelineTrendData.trendChartData;
    }

    private async _queryTaskFailureSummaryAndTrendData(queryOptions: ODataQueryOptions): Promise<IPipelineOutcomeTaskFailureData> {
        const data = await this.queryOData(queryOptions);
        if (useMockData) {
            return getMockTaskTrendData();
        }
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }
        let pipelineOutcomeTaskFailureData: IPipelineOutcomeTaskFailureData;
        const taskTrendDataMap: { [date: string]: IDailyTaskFailureData } = {};
        const orderedDates: string[] = [];
        const taskFailureData: { [key: string]: IFailingTaskData } = {};
        let totalFailedCount = 0;
        (data.value as IODataResult[]).forEach(datum => {
            const dateAsString = DateSKParser.parseDateSKAsDateString(datum.BuildCompletedDateSK || datum.PipelineRunCompletedDateSK);
            if (!taskTrendDataMap[dateAsString]) {
                taskTrendDataMap[dateAsString] = {
                    date: dateAsString,
                    taskFailures: []
                };

                orderedDates.push(dateAsString);
            }

            taskTrendDataMap[dateAsString].taskFailures.push({
                taskName: datum.TaskDisplayName,
                failedCount: datum.FailedCount
            });
            if (!taskFailureData[datum.TaskDisplayName]) {
                taskFailureData[datum.TaskDisplayName] = {
                    taskName: datum.TaskDisplayName,
                    failedCount: 0
                };
            }
            taskFailureData[datum.TaskDisplayName].failedCount += datum.FailedCount;
            totalFailedCount += datum.FailedCount;
        });
        pipelineOutcomeTaskFailureData = {
            trendChartData: orderedDates.map(date => taskTrendDataMap[date]),
            topFailingTasks: {
                failingTasksData: Object.keys(taskFailureData)
                    .map(taskData => {
                        return taskFailureData[taskData];
                    })
                    .sort((a, b) => b.failedCount - a.failedCount),
                failedCount: totalFailedCount
            }
        };
        // Return data as an ordered array
        return pipelineOutcomeTaskFailureData;
    }

    private async _queryStageFailureTrendData(
        queryOptions: ODataQueryOptions,
        confValues: ITrendChartReportConfiguration
    ): Promise<IPipelineOutcomeData> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }
        const primaryChartData: { [key: string]: ITrendChartAggregateData[] } = {};

        const chartData: ITrendChartData = {
            primaryChartData: { [this._emptyString]: [] }
        };

        const pipelineOutcomePassRateData: IPipelineOutcomePassRateData = {
            passPercentage: 0,
            failedCount: 0,
            totalCount: 0,
            passedCount: 0,
            failurePerStage: []
        } as IPipelineOutcomePassRateData;
        const pipelineOutcomeData: IPipelineOutcomeData = { trendChartData: chartData, pipelineOutcomePassRateData: pipelineOutcomePassRateData };
        const StageFailureData: { [key: string]: IStageMetrics } = {};
        // Iterate over all data
        (data.value as IODataStageFailureTrend[]).forEach(d => {
            const date = DateSKParser.parseDateSKAsDateString(d.BuildCompletedDateSK || d.PipelineRunCompletedDateSK);
            const failedCount = this._parseStageFailureCount(d);
            const stageName = this._parsePipelineStageName(d);
            const stageMetrics = this._parseStageData(stageName);
            if (!primaryChartData[stageMetrics.name]) {
                primaryChartData[stageMetrics.name] = [];
                StageFailureData[stageMetrics.name] = {
                    name: stageMetrics.name,
                    fullName: stageMetrics.fullName,
                    dataCount: 0
                };
            }

            primaryChartData[stageMetrics.name].push({
                date: date,
                aggrValue: failedCount
            });
            StageFailureData[stageMetrics.name].dataCount += failedCount;
        });
        pipelineOutcomeData.trendChartData.primaryChartData = primaryChartData;
        pipelineOutcomeData.pipelineOutcomePassRateData.failurePerStage = Object.keys(StageFailureData).map(stageData => {
            return StageFailureData[stageData];
        });
        const defaultStageData = primaryChartData[CommonResources.DefaultStageName];
        if (defaultStageData && defaultStageData.length > 1) {
            primaryChartData[CommonResources.DefaultStageName] = this._aggregateDefaultStageData(defaultStageData);
        }

        return pipelineOutcomeData;
    }

    private _aggregateDefaultStageData(stageData: ITrendChartAggregateData[]): ITrendChartAggregateData[] {
        const aggrDefaultStageData: ITrendChartAggregateData[] = [];
        let index = 0;
        aggrDefaultStageData.push(stageData[0]);
        for (let i = 1; i < stageData.length; i++) {
            if (stageData[i - 1].date === stageData[i].date) {
                aggrDefaultStageData[index].aggrValue += stageData[i].aggrValue;
            } else {
                aggrDefaultStageData.push(stageData[i]);
                index++;
            }
        }
        return aggrDefaultStageData;
    }
}
